import type {ChipSemanticType} from '@spaced-out/ui-design-system/lib/components/Chip/Chip';
import type {
  TreeNode,
  MultilevelFilterState,
} from 'src/types/analytics-genesis';
import {activeApplicationStatuses} from 'src/components/crm/constant';
import {convertFromRaw} from 'draft-js';
import {stateToHTML} from 'draft-js-export-html';
import type {
  ApplicationStage,
  Candidate,
  CandidateApplication,
  MATCHES_FILTER,
  Filter,
} from 'src/types/crm';


type Node = {
  id: string;
  name: string;
  city?: string;
  children: Node[];
};

export const getFormattedJobStatusWithSemantic = (
  status: string,
  t: (key: string, defaultValue: string) => string,
): {
  status: string;
  semantic: ChipSemanticType;
} => {
  if (status === 'DRAFT') {
    return {status: t('Draft', 'Draft'), semantic: 'primary'};
  } else if (status === 'APPROVAL_PENDING') {
    return {
      status: t('Approval_Pending', 'Approval Pending'),
      semantic: 'warning',
    };
  } else if (status === 'CLOSED') {
    return {status: t('Closed', 'Closed'), semantic: 'secondary'};
  } else if (status === 'CANCELLED') {
    return {status: t('Cancelled', 'Cancelled'), semantic: 'danger'};
  } else if (status === 'OPEN') {
    return {status: t('Open', 'Open'), semantic: 'information'};
  } else if (status === 'ONHOLD') {
    return {status: t('On_hold', 'On hold'), semantic: 'danger'};
  } else if (status === 'REJECTED') {
    return {
      status: t('Approval_Rejected', 'Approval Rejected'),
      semantic: 'danger',
    };
  } else if (status === 'TEMPLATE') {
    return {status: t('Template', 'Template'), semantic: 'warning'};
  } else if (status === 'FILLED') {
    return {status: t('Filled', 'Filled'), semantic: 'information'};
  } else if (status === 'PENDING_JOINER') {
    return {status: t('Pending_Joiner', 'Pending Joiner'), semantic: 'warning'};
  }

  return {status: '', semantic: 'primary'};
};

// To convert RANDY GOUSE => Randy Gouse (Initials Capital)
export const getTitleCaseFormattedText = (text: string = ''): string => {
  const formattedText = text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return formattedText;
};

export const convertTimestampToLocaleString = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString('en-US');
};

export const getExperienceDuration = ({
  startMonth,
  startYear,
  endMonth,
  endYear,
  isCurrent = false,
}: {
  startMonth: number;
  startYear: number;
  endMonth: number;
  endYear: number;
  isCurrent: boolean;
}): string => {
  const currentDate = new Date();
  const start = new Date(startYear, startMonth - 1);
  const end =
    isCurrent || (endMonth === -1 && endYear === -1)
      ? currentDate
      : new Date(endYear, endMonth - 1);

  const diffInMonths =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());

  const years = Math.floor(diffInMonths / 12);
  const months = diffInMonths % 12;

  const experienceDuration =
    years > 0
      ? `${years} yr${years > 1 ? 's' : ''} ${
          months > 0 ? `${months} month${months > 1 ? 's' : ''}` : ''
        }`
      : `${Math.max(months, 1)} month${months !== 1 ? 's' : ''}`;

  return experienceDuration;
};

export const getStageStatusFromApplicationStatus = (
  status: string,
  applicationStages: ApplicationStage[],
): {
  stageName: string;
  stageType: string;
  status: string;
} => {
  const activeStage = applicationStages.find(
    (stage) => stage.stageCandidateStatus === 'ACTIVE',
  );
  const stageStatus = status.split('_');
  let stageType = stageStatus[0];
  let stageName = stageType;

  if (applicationStages.length > 0 && activeStage) {
    stageType = activeStage.stageType;
    stageName = activeStage.stageName;
  }
  return {
    stageName,
    stageType,
    status: stageStatus.length > 1 ? stageStatus[1] : 'ACTIVE',
  };
};

export const isApplicationActive = (
  application: CandidateApplication,
): boolean => {
  return (
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"INTERVIEW" | "OFFER" | "HIRE" | "JOINED" | "INTERVIEW_ONHOLD" | "OFFER_ONHOLD" | "HIRE_ONHOLD" | "SHORTLISTED"'.
    activeApplicationStatuses.indexOf(application.status) > -1 &&
    !application.duplicate &&
    application.jobId > 0
  );
};

export const findActiveApplicationExcludingCurrentJob = (
  applications: CandidateApplication[],
  currentJobId: number,
): CandidateApplication | null => {
  const toCheckApplications = applications
    .filter((app) => app.jobId !== currentJobId)
    .sort((a, b) => b.updatedOn - a.updatedOn);
  return toCheckApplications.find(isApplicationActive) || null;
};

export const candidateActiveCheck = (
  candidates: Candidate[],
  jobId: number,
  toAction: string,
): {
  inActiveCandidateIds: number[];
  activeCandidatesData: {
    candidateId: number;
    activeJobName: string;
    candidateName: string;
    activeStageName: string;
    activeStageType: string;
    activeStatus: string;
    activeJobReason: string;
  }[];
} => {
  const inActiveCandidateIds: Array<number> = [],
    // @ts-expect-error - TS7034 - Variable 'activeCandidatesData' implicitly has type 'any[]' in some locations where its type cannot be determined.
    activeCandidatesData = [];

  candidates.forEach((candidate) => {
    let activeApplication = null;
    if (toAction === 'ADD' || toAction === 'MOVE') {
      activeApplication = candidate.applications?.find(
        (application: CandidateApplication) =>
          application.duplicate === false &&
          // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"INTERVIEW" | "OFFER" | "HIRE" | "JOINED" | "INTERVIEW_ONHOLD" | "OFFER_ONHOLD" | "HIRE_ONHOLD" | "SHORTLISTED"'.
          activeApplicationStatuses.indexOf(application.status) > -1,
      );
    } else {
      activeApplication = findActiveApplicationExcludingCurrentJob(
        candidate.applications || [],
        jobId,
      );
    }

    if (!activeApplication) {
      inActiveCandidateIds.push(candidate.id);
    }

    if (activeApplication) {
      const activeStatus = activeApplication.status;
      const applicationStages = activeApplication.stages;
      const {stageName, stageType, status} =
        getStageStatusFromApplicationStatus(activeStatus, applicationStages);

      const data = {
        activeJobName: activeApplication.jobTitle,
        candidateId: candidate.id,
        candidateName: candidate.resume.name || '',
        activeStageName: stageName,
        activeStageType: stageType,
        activeStatus: status,
        activeJobReason: activeApplication.reason || '',
      } as const;
      activeCandidatesData.push(data);
    }
  });

  // @ts-expect-error - TS7005 - Variable 'activeCandidatesData' implicitly has an 'any[]' type.
  return {inActiveCandidateIds, activeCandidatesData};
};

export const getBulkActionFilters = ({
  fromAction,
  bulkActionFilters,
  jobId,
}: {
  fromAction: string;
  bulkActionFilters: MATCHES_FILTER | Filter;
  jobId: number;
}) => {
  return fromAction === 'CANDIDATES'
    ? {
        matchingParameter: {
          searchFilter: bulkActionFilters,
          candidateCustomFilters:
            // @ts-expect-error - TS2339 - Property 'candidateCustomFilters' does not exist on type 'Filter | MATCHES_FILTER'.
            bulkActionFilters?.candidateCustomFilters || [],
          jobCandidateCustomFilters:
            // @ts-expect-error - TS2339 - Property 'jobCandidateCustomFilters' does not exist on type 'Filter | MATCHES_FILTER'.
            bulkActionFilters?.jobCandidateCustomFilters,
        },
      }
    : fromAction === 'SCREEN' || fromAction === 'TALENT_POOL'
    ? {
        jobId,
        matchingParameter: {
          ...bulkActionFilters,
          candidateCustomFilters:
            // @ts-expect-error - TS2339 - Property 'candidateCustomFilters' does not exist on type 'Filter | MATCHES_FILTER'.
            bulkActionFilters?.candidateCustomFilters || [],
          jobCandidateCustomFilters:
            // @ts-expect-error - TS2339 - Property 'jobCandidateCustomFilters' does not exist on type 'Filter | MATCHES_FILTER'.
            bulkActionFilters?.jobCandidateCustomFilters || [],
        },
      }
    : fromAction === 'INTERVIEW'
    ? {milestoneCandidateWithStatusForm: bulkActionFilters}
    : fromAction === 'ONHOLD' || fromAction === 'REJECT'
    ? {nonInterviewCandidateWithStatusForm: bulkActionFilters}
    : fromAction === 'HIRED'
    ? {stageTypeCandidatesForm: bulkActionFilters}
    : {};
};

export const downloadCsvFile = (response: Blob, fileName: string) => {
  const blob = new Blob([response], {type: 'text/csv'});
  const url = URL.createObjectURL(blob);

  const link = Object.assign(document.createElement('a'), {
    href: url,
    download: fileName,
  });
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const transformTreeOptions = (
  trees: Array<Node> = [],
): Array<TreeNode> => {
  if (!trees) {
    return [];
  }

  const result: Array<TreeNode> = [];

  // Process each root-level tree node.
  trees.forEach((tree: Node) => {
    const newTree: TreeNode = {
      key: tree.id,
      label: tree.name,
      children: [],
    };
    result.push(newTree);

    // The stack holds pairs { source, target } to process each node iteratively.
    const stack: Array<{
      source: Node;
      target: TreeNode;
    }> = [{source: tree, target: newTree}];

    // Iteratively traverse the tree.
    while (stack.length > 0) {
      // @ts-expect-error - TS2339 - Property 'source' does not exist on type '{ source: Node; target: TreeNode; } | undefined'. | TS2339 - Property 'target' does not exist on type '{ source: Node; target: TreeNode; } | undefined'.
      const {source, target} = stack.pop();

      if (Array.isArray(source.children) && source.children.length > 0) {
        // Map the source children to transformed children.
        target.children = source.children.map((child: Node) => ({
          key: child.id,
          label: child.name,
          children: [],
        }));

        // Push each pair onto the stack for further processing.
        for (let i = 0; i < source.children.length; i++) {
          stack.push({source: source.children[i], target: target.children[i]});
        }
      }
    }
  });

  return result;
};

export const initializeTreeState = (
  treeOptions: Array<TreeNode>,
  selectedKeys: Array<string>,
): MultilevelFilterState => {
  const selectedKeysSet = new Set(selectedKeys);
  const indeterminateValues = getIndeterminateNodes(
    {key: '__root__', children: treeOptions},
    selectedKeysSet,
  );

  return {
    currentNode: {
      key: '__root__',
      label: 'Root',
      children: treeOptions,
    },
    history: [],
    indeterminateValues: Array.from(indeterminateValues),
  };
};

export const defaultTreeState: MultilevelFilterState = {
  currentNode: {key: '__root__', label: 'Root', children: []},
  history: [],
  indeterminateValues: [],
};

export const getIndeterminateNodes = (
  node: TreeNode,
  selectedKeys: Set<string>,
): Set<string> => {
  if (!node.children || node.children.length === 0) {
    return new Set();
  }

  const selectedChildren = node.children.filter((child) =>
    selectedKeys.has(child.key),
  );
  const childIndeterminateValues = new Set();

  node.children.forEach((child) => {
    const childIndeterminate = getIndeterminateNodes(child, selectedKeys);
    childIndeterminate.forEach((value) => childIndeterminateValues.add(value));
  });

  if (
    selectedChildren.length > 0 &&
    selectedChildren.length < node.children.length
  ) {
    childIndeterminateValues.add(node.key);
  }

  // @ts-expect-error - TS2322 - Type 'Set<unknown>' is not assignable to type 'Set<string>'.
  return childIndeterminateValues;
};

export const convertToHierarchy = (flatData: any) => {
  if (!flatData) {
    return [];
  }
  // Create a map of all items by id
  const itemMap: Record<string, any> = {};
  // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
  flatData.forEach((item) => {
    itemMap[item.id] = {
      children: [],
      key: item.id,
      label: item.name,
    };
  });
  // Build the tree structure
  const rootItems: Array<never> = [];
  // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
  flatData.forEach((item) => {
    const mappedItem = itemMap[item.id];
    if (item.parentId && itemMap[item.parentId]) {
      // This item has a parent in our data, add it to parent's children
      itemMap[item.parentId].children.push(mappedItem);
    } else {
      // This is a root item
      // @ts-expect-error - TS2345 - Argument of type 'any' is not assignable to parameter of type 'never'.
      rootItems.push(mappedItem);
    }
  });
  return rootItems;
};

export const getHostURL = (): string => {
  return (
    window.location.protocol +
    '//' +
    window.location.hostname +
    (window.location.port ? ':' + window.location.port : '')
  );
};
export const URL_REDIRECTION_NEW_CRM_MAPPING = {
  '/jobs/:jobId/candidates/:candidateId':
    '/crm/job/:jobId/info/applicants/:candidateId',
  '/jobs/:jobId/about/job-details': '/crm/job/:jobId/info/details',
  '/jobs/:jobId': '/crm/job/:jobId/info/details',
  '/jobs/:jobId/about/hiring-team': '/crm/job/:jobId/info/details',
  '/candidates/:candidateId':
    '/crm/candidates/:candidateId?selectedTab=overview&subTab=details',
  '/jobs/:jobId/candidates/:candidateId/jobs/stages':
    '/crm/job/:jobId/info/applicants/:candidateId?selectedTab=overview&subTab=details',
  '/jobs/:jobId/candidates/:candidateId/jobs/feedbacks':
    '/crm/job/:jobId/info/applicants/:candidateId?selectedTab=feedbacks',
  '/jobs/:jobId/candidates/screen':
    '/crm/job/:jobId/info/applicants?selectedStageType=SCREENING',
  '/candidates/:candidateId/about':
    '/crm/candidates/:candidateId?selectedTab=overview&subTab=details',
  '/jobs/:jobId/candidates/:candidateId?subTab=evaluation':
    '/crm/job/:jobId/info/applicants/:candidateId?selectedTab=overview&subTab=evaluation',
} as const;

export function getRedirectPath(inputUrl: string): string | null {
  const trimmedUrl = inputUrl.replace(/trm#/, '');
  for (const [pattern, redirectPattern] of Object.entries(
    URL_REDIRECTION_NEW_CRM_MAPPING,
  )) {
    const regexPattern = new RegExp(
      '^' +
        pattern
          .replace(/:jobId/g, '(\\d+)') // Ensure jobId is numeric
          .replace(/:candidateId/g, '(\\d+)')
          .replace(/[?=&]/g, '\\$&') +
        '$', // Ensure candidateId is numeric
    );
    const match = trimmedUrl.match(regexPattern);
    if (match) {
      let redirectedUrl = redirectPattern;
      const keys = [...pattern.matchAll(/:([^/?=&]+)/g)].map((m) => m[1]); // Extract keys like jobId, candidateId
      keys.forEach((key, index) => {
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type '"/crm/job/:jobId/info/applicants/:candidateId" | "/crm/job/:jobId/info/details" | "/crm/candidates/:candidateId?selectedTab=overview" | "/crm/job/:jobId/info/applicants/:candidateId?selectedTab=overview" | "/crm/job/:jobId/info/applicants/:candidateId?selectedTab=feedbacks" | "/crm/job/:jobId/info/applicants?selecte...'.
        redirectedUrl = String(redirectedUrl).replace(
          `:${key}`,
          match[index + 1],
        );
      });
      console.log('redirectedUrl', redirectedUrl);
      return String(redirectedUrl);
    }
  }
  return null; // No matching pattern found
}

const removeEmptyBlocks = (rawContent: any) => {
  // Create a deep copy to avoid mutating the input
  const contentCopy = JSON.parse(JSON.stringify(rawContent));

  // Check if blocks exist before filtering
  if (contentCopy && Array.isArray(contentCopy.blocks)) {
    contentCopy.blocks = contentCopy.blocks.filter(
      // @ts-expect-error - TS7006 - Parameter 'block' implicitly has an 'any' type.
      (block) => block && block.text && block.text.trim() !== '',
    );
  }

  return contentCopy;
};

// Define customRenderOptions
const customRenderOptions = {
  inlineStyles: {
    BOLD: {element: 'strong'},
    ITALIC: {element: 'em'},
    UNDERLINE: {element: 'u'},
  },
  // @ts-expect-error - TS7030 - Not all code paths return a value. | TS7006 - Parameter 'entity' implicitly has an 'any' type.
  entityStyleFn: (entity) => {
    const entityType = entity.get('type').toLowerCase();

    if (entityType === 'link') {
      const data = entity.getData();
      const fullUrl =
        data.url.startsWith('http://') || data.url.startsWith('https://')
          ? data.url
          : `https://${data.url}`;
      return {
        element: 'a',
        attributes: {
          href: fullUrl,
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      };
    }
  },
} as const;

export const renderAsHTML = (jsonString: string): string => {
  if (!jsonString) {
    return '';
  }

  try {
    // Parse the JSON string to get the raw content
    const rawContent = JSON.parse(jsonString);

    // Remove empty blocks while preserving the original content
    const cleanedContent = removeEmptyBlocks(rawContent);

    // Convert the raw content to a ContentState object
    const contentState = convertFromRaw(cleanedContent);

    // Convert the ContentState to HTML using custom render options
    return stateToHTML(contentState, customRenderOptions);
  } catch (error: any) {
    console.error('Error rendering Draft.js content as HTML:', error);

    // Instead of returning the JSON string, return a fallback message
    // or an empty string depending on requirements
    return typeof jsonString === 'string' ? jsonString : '';
  }
};

export const getSocialMediaIcon = (site: string): string => {
  switch (site) {
    case 'ANGEL':
      return 'angellist';
    case 'BEHANCE':
      return 'behance';
    case 'BLOGGER':
      return 'blogger';
    case 'DRIBBBLE':
      return 'dribbble';
    case 'FACEBOOK':
      return 'facebook';
    case 'FLICKR':
      return 'flickr';
    case 'GITHUB':
      return 'github';
    case 'GOOGLEPLUS':
      return 'google-plus';
    case 'LINKEDIN':
      return 'linkedin';
    case 'PINTEREST':
      return 'pinterest';
    case 'STACKOVERFLOW':
      return 'stack-overflow';
    case 'TWITTER':
      return 'twitter';
    case 'WORDPRESS':
      return 'wordpress';
    case 'YOUTUBE':
      return 'youtube';
    default:
      return 'link';
  }
};

export const getActiveStatusFromStageType = (stageType: string): string[] => {
  switch (stageType) {
    case 'SCREENING':
      return ['SCREEN'];
    case 'GENERAL':
      return ['INTERVIEW'];
    case 'OFFERED':
      return ['OFFER'];
    case 'HIRED':
      return ['HIRE'];
    default:
      return [];
  }
};
